import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import VBox from 'components/common/VBox';
import HBox from 'components/common/HBox';
import FocusableButton from 'components/common/FocusableButton';
import Input from 'components/common/Input';
import SelectButton from 'components/common/SelectButton';
import React from 'react';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import useForm, { defaultPasswordValidatingOptions } from 'hooks/common/useForm';
import * as Luxon from 'luxon';
import Textarea from 'components/common/Textarea';
import useDoctorGuide from 'hooks/communication/useDoctorGuide';
import useDoctorGuideSave from 'hooks/communication/useDoctorGuideSave';
import useMessageBox from 'hooks/app/useMessageBox';

import exercise_image from 'images/exercise.png';
import diet_image from 'images/diet.png';
import insulin_image from 'images/insulin.png';

const images = {
  exercise: exercise_image,
  diet: diet_image,
  insulin: insulin_image,
}

const guides = {
  'exercise': {
    label: T('활동'),
    published: 'active_guide',
  },
  'diet': {
    label: T('식사'),
    published: 'meal_guide',
  },
  'insulin': {
    label: T('인슐린'),
    published: 'insulin_guide',
  },
};

export default function DrGuideDialog(props) {
  const {
    category,

    index,
    count,
    onPrev,
    onNext,
    events,

    type,
    ...rest
  } = props;

  const [guide, setGuide] = React.useState({});

  const doctor_info = Recoil.useRecoilValue(AppStore.assets.doctor_info);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  const {
    dr_id,
  } = doctor_info || {};

  const {
    data,
    errors,
    register,
    validate,
    onSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      contents: '',
      guide_time: '',
    },
  });

  const {
    onDoctorGuideSave,
  } = useDoctorGuideSave();

  const {
    requestDoctorGuide,
  } = useDoctorGuide();

  const [reload, setReload] = Recoil.useRecoilState(AppStore.assets.guide_reload);
  React.useEffect(() => {
    requestDoctorGuide({
      dr_id,
      patient_no,
      new_flag_yn: 'N',
    }, (success, data, parameters) => {
      if (success) {
        const sorted = data.published.sort((a, b) => a.send_time < b.send_time ? 1 : -1);
        const active_guide = sorted.filter(guide => guide.active_guide)[0];
        const meal_guide = sorted.filter(guide => guide.meal_guide)[0];
        const insulin_guide = sorted.filter(guide => guide.insulin_guide)[0];
        const guide = {
          draft: data.draft,
          published: {
            active_guide,
            meal_guide,
            insulin_guide,
          }
        }
        setGuide(() => guide);
        setValue('contents', data.draft[guides[type]?.published] || '');
        setValue('guide_time', data.draft.guide_time);
      }
    });
  }, [dr_id, patient_no, setValue, type, reload, requestDoctorGuide]);

  const onCopy = React.useCallback(() => {
    setValue('contents', (((guide.published || {})[guides[type]?.published] || {})[guides[type]?.published]) || '');
  }, [guide, setValue, type]);

  const {
    open,
    update,
    close,
  } = useMessageBox(category);

  const onSaved = React.useCallback((success, data, parameters) => {
    if (success) {
      setReload(r => r + 1);
      events?.onClose();
    } else {
      open({
        id: 'save',
        title: T('Dr.Guide 저장 실패'),
        contents: (
          <div>
            {data.message}
          </div>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => close('save'),
        },
      });
    }
  }, [close, events, open, setReload]);

  return (
    <VBox
      {...ex(['Dialog', 'DrGuideDialog'], rest)}
    >
      <HBox {...ex('DialogTitle')}>
        <div>
          {T('Dr.Guide 작성')}
        </div>
        <div>
          <FocusableButton
            {...{
              onClick: events?.onClose,
            }}
          >
            <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M6.29279 6.29308C6.48031 6.10561 6.73462 6.00029 6.99979 6.00029C7.26495 6.00029 7.51926 6.10561 7.70679 6.29308L11.9998 10.5861L16.2928 6.29308C16.385 6.19757 16.4954 6.12139 16.6174 6.06898C16.7394 6.01657 16.8706 5.98898 17.0034 5.98783C17.1362 5.98668 17.2678 6.01198 17.3907 6.06226C17.5136 6.11254 17.6253 6.18679 17.7192 6.28069C17.8131 6.37458 17.8873 6.48623 17.9376 6.60913C17.9879 6.73202 18.0132 6.8637 18.012 6.99648C18.0109 7.12926 17.9833 7.26048 17.9309 7.38249C17.8785 7.50449 17.8023 7.61483 17.7068 7.70708L13.4138 12.0001L17.7068 16.2931C17.8889 16.4817 17.9897 16.7343 17.9875 16.9965C17.9852 17.2587 17.88 17.5095 17.6946 17.6949C17.5092 17.8803 17.2584 17.9855 16.9962 17.9878C16.734 17.99 16.4814 17.8892 16.2928 17.7071L11.9998 13.4141L7.70679 17.7071C7.51818 17.8892 7.26558 17.99 7.00339 17.9878C6.74119 17.9855 6.49038 17.8803 6.30497 17.6949C6.11956 17.5095 6.01439 17.2587 6.01211 16.9965C6.00983 16.7343 6.11063 16.4817 6.29279 16.2931L10.5858 12.0001L6.29279 7.70708C6.10532 7.51955 6 7.26525 6 7.00008C6 6.73492 6.10532 6.48061 6.29279 6.29308Z" fill="#5B6871" />
            </svg>
          </FocusableButton>
        </div>
      </HBox>
      <HBox>
        <div>
          <img src={images[type]} alt={type} />
        </div>
        <form {...{ onSubmit: onSubmit(onDoctorGuideSave({ dr_id, patient_no, send_yn: 'N', [guides[type]?.published]: data['contents'] }, onSaved)) }}>
          <VBox {...ex('DrGuideBox')} full>
            <div>
              <div {...ex(['GuideTag', 'exercise'])}>
                {guides[type].label}
              </div>
              <div {...ex('GuideDate')}>
                {((guide?.published || {})[guides[type].published] || {})?.send_time && T('{0} 발행', Luxon.DateTime.fromFormat(((guide?.published || {})[guides[type].published] || {})?.send_time, 'yyyyMMddHHmmss').toFormat('yy.MM.dd'))}
              </div>
            </div>
            <HBox {...ex('GuideContents')} full>
              {((guide?.published || {})[guides[type].published] || {})[[guides[type].published]]}
            </HBox>
          </VBox>
          <VBox full>
            <Textarea
              {...register('contents', {
                type: 'text',
                autoComplete: 'off',
                placeholder: T('{0} 닥터가이드를 입력하세요.', guides[type]?.label),
              })}
              {...ex({ error: errors['contents'] })}
            >
              {errors['contents'] && (
                <div {...ex('ErrorMessage')}>
                  {errors['contents']}
                </div>
              )}
            </Textarea>
          </VBox>
          <HBox center>
            <FocusableButton
              {...ex(['CopyButton', 'secondary'], {
                type: 'button',
                onClick: onCopy,
              })}
            >
              {T('지난글 복사')}
            </FocusableButton>
            <FocusableButton
              {...ex(['SendButton', 'primary'], {
                type: 'submit',
              })}
            >
              {T('저장하기')}
            </FocusableButton>
          </HBox>
        </form>
      </HBox>
    </VBox >
  );
}
